import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workspace } from '@lawbee/domain';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
} from '@lawbee/ui';
import { Trash2, UserPlus, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { UpgradeAction } from '../../components/PlanGate';
import { api, errorMessage, isApiError } from '../../lib/api';
import { useApiMutation } from '../../lib/hooks';
import { can, ME_KEY, useMe } from '../../lib/me';
import type { Role, TeamResponse } from '../../lib/types';
import { CopyField, ReadOnlyNote } from '../app/ops/shared';
import { SettingsCard } from './layout';

const TEAM_KEY = ['team'] as const;
const ROLE_OPTIONS = workspace.ROLES.map((r) => ({ value: r, label: workspace.ROLE_LABELS[r] }));

export function TeamSettingsPage() {
  const me = useMe();
  const canEdit = can(me, 'team');
  const q = useQuery({ queryKey: TEAM_KEY, queryFn: () => api.get<TeamResponse>('/api/workspace/team') });
  const [inviteOpen, setInviteOpen] = useState(false);

  if (q.isLoading) return <LoadingState label="Loading team" />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { members, invitations, seats } = q.data;
  const full = seats.limit !== null && seats.used >= seats.limit;

  return (
    <div className="space-y-6">
      <SettingsCard
        title="Team members"
        description={
          <>
            {seats.limit === null ? (
              'Unlimited seats'
            ) : (
              <>
                <span className="figures">{seats.used}</span> of <span className="figures">{seats.limit}</span> seats used
              </>
            )}
            . Pending invitations count toward your seats.
          </>
        }
        footer={
          canEdit ? (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus /> Invite member
            </Button>
          ) : undefined
        }
      >
        <div className="space-y-4">
          {!canEdit ? <ReadOnlyNote>Only an owner can invite people or change roles.</ReadOnlyNote> : null}
          {full && canEdit ? (
            <Alert variant="warning" title="All seats are in use">
              <span className="block">Upgrade your plan to add more people.</span>
              <span className="mt-2 block">
                <UpgradeAction me={me} size="sm" />
              </span>
            </Alert>
          ) : null}
          <MembersTable members={members} canEdit={canEdit} currentUserId={me.user.id} />
        </div>
      </SettingsCard>

      <SettingsCard title="Pending invitations" description="Invitations expire after 14 days.">
        {invitations.length === 0 ? (
          <EmptyState icon={<Users />} title="No pending invitations" className="py-8" />
        ) : (
          <ul className="divide-y divide-border/70">
            {invitations.map((inv) => (
              <InvitationRow key={inv.id} invitation={inv} canEdit={canEdit} />
            ))}
          </ul>
        )}
      </SettingsCard>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        {inviteOpen ? <InviteForm onClose={() => setInviteOpen(false)} /> : null}
      </Dialog>
    </div>
  );
}

function MembersTable({ members, canEdit, currentUserId }: { members: TeamResponse['members']; canEdit: boolean; currentUserId: string }) {
  const qc = useQueryClient();
  const [removing, setRemoving] = useState<TeamResponse['members'][number] | null>(null);
  const changeRole = useApiMutation(({ id, role }: { id: string; role: Role }) => api.patch(`/api/workspace/team/members/${id}`, { role }), {
    success: 'Role updated',
    invalidate: [TEAM_KEY],
    onSuccess: () => void qc.invalidateQueries({ queryKey: ME_KEY }),
  });
  const remove = useApiMutation((id: string) => api.del(`/api/workspace/team/members/${id}`), {
    success: 'Member removed',
    invalidate: [TEAM_KEY, ['billing']],
    onSuccess: () => setRemoving(null),
  });

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((m) => {
            const self = m.userId === currentUserId;
            return (
              <TableRow key={m.id}>
                <TableCell>
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={m.name || m.email} size="sm" />
                    <div className="min-w-0">
                      <p className="truncate font-medium">
                        {m.name || m.email}
                        {self ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">(you)</span> : null}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="w-40">
                  {canEdit ? (
                    <Select
                      aria-label={`Role for ${m.name || m.email}`}
                      className="w-36"
                      value={m.role}
                      options={ROLE_OPTIONS}
                      disabled={changeRole.isPending}
                      onChange={(e) => changeRole.mutate({ id: m.id, role: e.target.value as Role })}
                    />
                  ) : (
                    <Badge variant={m.role === 'owner' ? 'primary' : 'default'}>{workspace.ROLE_LABELS[m.role] ?? m.role}</Badge>
                  )}
                </TableCell>
                <TableCell className="figures whitespace-nowrap text-muted-foreground">{formatDate(m.createdAt)}</TableCell>
                <TableCell className="text-right">
                  {canEdit && !self ? (
                    <Button variant="ghost" size="icon" className="size-8" aria-label={`Remove ${m.name || m.email}`} onClick={() => setRemoving(m)}>
                      <Trash2 />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.name || removing?.email || 'member'}?`}
        description="They lose access to this firm right away. Matters and notes they created stay."
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </>
  );
}

function InvitationRow({ invitation: inv, canEdit }: { invitation: TeamResponse['invitations'][number]; canEdit: boolean }) {
  const revoke = useApiMutation(() => api.del(`/api/workspace/team/invitations/${inv.id}`), { success: 'Invitation revoked', invalidate: [TEAM_KEY, ['billing']] });
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{inv.email}</p>
        <p className="text-xs text-muted-foreground">
          {workspace.ROLE_LABELS[inv.role] ?? inv.role} · expires <span className="figures">{formatDate(inv.expiresAt)}</span>
        </p>
      </div>
      {canEdit ? (
        <Button variant="outline" size="sm" className="self-start sm:self-center" loading={revoke.isPending} onClick={() => revoke.mutate(undefined)}>
          Revoke
        </Button>
      ) : null}
    </li>
  );
}

function InviteForm({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('lawyer');
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState<string | null>(null);
  const [link, setLink] = useState<{ email: string; link: string } | null>(null);
  const invite = useApiMutation((body: { email: string; role: Role }) => api.post<{ ok: boolean; invitation: { id: string; email: string; role: Role; link: string } }>('/api/workspace/team/invite', body), {
    success: 'Invitation sent',
    invalidate: [TEAM_KEY, ['billing']],
    onSuccess: (d) => setLink({ email: d.invitation.email, link: d.invitation.link }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError('Enter a valid email address.');
    setError(null);
    setLimit(null);
    invite.mutate(
      { email: email.trim(), role },
      {
        onError: (err) => {
          if (isApiError(err, 'limit_reached')) setLimit(err.message);
          else setError(errorMessage(err));
        },
      },
    );
  };

  if (link) {
    return (
      <DialogContent title="Invitation sent" description={`We emailed ${link.email} a link to join.`} footer={<Button onClick={onClose}>Done</Button>}>
        <CopyField label="Invitation link" value={link.link} description="You can also send this link yourself. It expires in 14 days." />
      </DialogContent>
    );
  }

  return (
    <DialogContent
      title="Invite a team member"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="invite-form" loading={invite.isPending}>
            Send invitation
          </Button>
        </>
      }
    >
      <form id="invite-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Email" htmlFor="inv-email" required error={error}>
          <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@yourfirm.ca" autoComplete="off" aria-invalid={!!error || undefined} />
        </Field>
        <Field label="Role" htmlFor="inv-role" description="Owners manage settings, billing and the team. Lawyers approve client updates and conflict checks. Staff handle intake and scheduling.">
          <Select id="inv-role" value={role} onChange={(e) => setRole(e.target.value as Role)} options={ROLE_OPTIONS} />
        </Field>
        {limit ? (
          <Alert variant="warning" title="Seat limit reached">
            <span className="block">{limit}</span>
            <span className="mt-2 block">
              <UpgradeAction me={me} size="sm" />
            </span>
          </Alert>
        ) : null}
      </form>
    </DialogContent>
  );
}
